/**
 * FuarBilgisiModal.jsx — "Fuar Bilgisi" penceresi: bir fuarın salt okunur özeti (backlog #561).
 *
 * Fuarın adı (oturumun dilinde), fuar alanı ve aşamaların tarih-saatleri: belge kabul, dijital belge kabul, kurulum, fuar, söküm. Şu an
 * süren aşama vurgulanır. Üst menüdeki "Fuar Seç" penceresinin ve Fuar Listesi'nin fuar ayrıntısı düğmesi açar: katılımcı (firma) girişinde
 * ve page.fuar'ı olmayan personelde de çalışır, çünkü veri sayfa yetkisinden bağımsız.
 *
 * `fuar`, `GET /fuar/list` / `GET /fuar/selectable` öğesidir (FuarListDTO; her ikisi de oturumdan kapsamlı: personel tüm fuarları, firma
 * yalnızca firmasının fuarlarını görür) — pencere kendi başına istek atmaz, yeni uç yok. (`GET /fuar/{id}` ve detay uçları oturumsuz açık
 * ve çok fazla veri döner, kullanılmıyor.) page.fuar'ı olana "Düzenle" bağlantısı çıkar (düzenleme formu /Management/Fairs/:id).
 */
import React from "react";
import { Link } from "react-router-dom";
import { Badge, Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import { useTranslation } from "react-i18next";
import FuarLogoImg from "./FuarLogoImg";
import { asamaDurumu, fuarAsamalari, tarihSaatMetni } from "./fuarAsamalari";
import { isFirmaAccount } from "../../services/firmaSessionService";
import { SAYFA_IZNI, useYetki } from "../../services/rbacService";

// Yalnızca personele çizilir (firma girişi /rbac/me'yi boşuna çağırmasın); page.fuar yoksa hiçbir şey görünmez.
function FuarDuzenleDugmesi({ fuarId, onClick }) {
  const { t } = useTranslation("common");
  const yetki = useYetki(SAYFA_IZNI.FUAR);
  if (yetki !== true) return null;
  return (
    <Button tag={Link} to={`/Management/Fairs/${fuarId}`} onClick={onClick} color="primary" outline>
      <i className="bx bx-edit me-1" />{t("duzenle")}
    </Button>
  );
}

export default function FuarBilgisiModal({ fuar, isOpen, toggle }) {
  const { t } = useTranslation("yonetim");
  const { t: tc } = useTranslation("common");
  const asamalar = fuarAsamalari(fuar);

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg" scrollable>
      <ModalHeader toggle={toggle}>{t("fuarBilgisi.baslik")}</ModalHeader>
      <ModalBody>
        {fuar && (
          <>
            <div className="d-flex align-items-center gap-3 flex-wrap mb-3">
              <div className="d-flex align-items-center justify-content-center bg-light rounded p-2" style={{ width: 96, height: 72 }}>
                <FuarLogoImg
                  url={fuar.fuarLogoUrl} fileName={fuar.fuarLogo} alt={fuar.fuarName}
                  style={{ maxHeight: 56, maxWidth: "100%", objectFit: "contain" }}
                />
              </div>
              <div className="flex-grow-1">
                <h5 className="mb-1">{fuar.fuarName}</h5>
                {fuar.fuarAlaniName && <div className="text-muted"><i className="bx bx-map me-1" />{fuar.fuarAlaniName}</div>}
                {fuar.fuarDescription && <p className="text-muted small mb-0 mt-1">{fuar.fuarDescription}</p>}
              </div>
            </div>

            <h6 className="text-muted text-uppercase fs-12 mb-2">{t("fuarBilgisi.asamalar")}</h6>
            <ul className="list-group" data-testid="fuar-asamalari">
              {asamalar.map((a) => {
                const durum = asamaDurumu(a);
                const soluk = durum === "gecmis" ? "text-muted" : "";
                return (
                  <li
                    key={a.anahtar} data-asama={a.anahtar} data-durum={durum || ""}
                    className={`list-group-item py-3 ${durum === "suruyor" ? "bg-primary-subtle border-primary" : ""}`}
                  >
                    <Row className="align-items-center g-2">
                      <Col md={4} className={`d-flex align-items-center gap-3 ${soluk}`}>
                        <span className="avatar-xs flex-shrink-0">
                          <span className={`avatar-title rounded ${durum === "suruyor" ? "bg-primary text-white" : "bg-light text-muted"}`}>
                            <i className={`bx ${a.simge} fs-5`} />
                          </span>
                        </span>
                        <div>
                          <div className="fw-semibold">{t(`fuarBilgisi.${a.anahtar}`)}</div>
                          {a.anahtar === "belgeKabul" && <div className="text-muted small">{t("fairsform.sistemIci")}</div>}
                          {a.anahtar === "dijitalBelgeKabul" && <div className="text-muted small">{t("fairsform.sistemDisi")}</div>}
                        </div>
                      </Col>
                      <Col md={3} xs={6} className={soluk}>
                        <div className="text-muted small">{t("fuarBilgisi.baslangic")}</div>
                        <div>{tarihSaatMetni(a.ilk)}</div>
                      </Col>
                      <Col md={3} xs={6} className={soluk}>
                        <div className="text-muted small">{t("fuarBilgisi.bitis")}</div>
                        <div>{tarihSaatMetni(a.son)}</div>
                      </Col>
                      <Col md={2} className="text-md-end">
                        {durum === "suruyor" && <Badge color="primary">{t("fuarBilgisi.suruyor")}</Badge>}
                        {durum === "gecmis" && <span className="text-muted small"><i className="bx bx-check-circle me-1" />{t("fuarBilgisi.gecmis")}</span>}
                        {durum === "gelecek" && <Badge color="light" className="text-muted border">{t("fuarBilgisi.gelecek")}</Badge>}
                      </Col>
                    </Row>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {fuar && !isFirmaAccount() && <FuarDuzenleDugmesi fuarId={fuar.fuarId} onClick={toggle} />}
        <Button color="light" onClick={toggle}>{tc("kapat")}</Button>
      </ModalFooter>
    </Modal>
  );
}
